import React, { useState } from 'react';
import { Shield, Lock, FileText, Mail, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

export function Profile() {
  const { user } = useAuth();
  
  // Tab control for policies
  const [activeTab, setActiveTab] = useState<'TERMS' | 'PRIVACY' | 'REFUND'>('TERMS');

  // Change password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Eye icon visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.post('/tenant/users/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: { 'x-tenant-id': user?.tenantId }
      });

      if (response.data.success) {
        setSuccessMsg('Your account password has been updated successfully.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password. Please check your credentials.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 text-slate-800 font-sans antialiased max-w-5xl mx-auto">
      {/* Header */}
      <div className="border-b border-slate-200 pb-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">My Profile & Policies</h1>
        <p className="text-xs text-slate-500 mt-1">
          Manage your personal POS credentials and view legal terms, privacy guidelines, and refund compliance.
        </p>
      </div>

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-250 text-xs text-emerald-800 font-semibold rounded-xl flex items-center gap-2.5">
          <CheckCircle size={16} className="text-emerald-600" />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold rounded-xl flex items-center gap-2.5">
          <AlertCircle size={16} className="text-rose-600" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Profile Card & Password change Form (Left 1/3) */}
        <div className="lg:col-span-1 space-y-6">
          {/* Identity Card */}
          <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm space-y-4">
            <div className="flex items-center space-x-3.5 pb-3.5 border-b border-slate-100">
              <div className="h-12 w-12 rounded-xl bg-primary-600 flex items-center justify-center text-white text-base font-extrabold shadow">
                {user?.firstName?.slice(0, 2).toUpperCase() || 'US'}
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">{user?.firstName} {user?.lastName}</h3>
                <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider block mt-0.5">
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="space-y-2 text-xs font-semibold text-slate-600">
              <div className="flex justify-between">
                <span className="text-slate-400">Email Login:</span>
                <span className="text-slate-800">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Store Workspace:</span>
                <span className="text-slate-800">{user?.tenantName || 'Platform Host'}</span>
              </div>
            </div>
          </div>

          {/* Change Password Form */}
          <div className="card bg-white border border-slate-150 p-5 rounded-xl shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-1.5">
              <Lock className="text-slate-500" size={14} />
              <span>Update Credentials</span>
            </h3>

            <form onSubmit={handlePasswordChange} className="space-y-4 text-xs font-medium">
              {/* Current Password */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block">Current Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showCurrent ? "text" : "password"}
                    required
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 text-slate-400 hover:text-slate-650 cursor-pointer"
                  >
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block">New Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showNew ? "text" : "password"}
                    required
                    placeholder="Min. 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 text-slate-400 hover:text-slate-650 cursor-pointer"
                  >
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block">Confirm New Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showConfirm ? "text" : "password"}
                    required
                    placeholder="Verify new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 text-slate-400 hover:text-slate-650 cursor-pointer"
                  >
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className="w-full py-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white font-bold rounded-lg disabled:opacity-50 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Updating credentials...</span>
                  </>
                ) : (
                  <>
                    <Shield size={14} />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Policies Viewer (Right 2/3) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card bg-white border border-slate-150 p-6 rounded-xl shadow-sm space-y-5 h-full">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-1.5">
              <FileText className="text-primary-600" size={15} />
              <span>TRIA POS Governance & Legals</span>
            </h3>

            {/* Tab Controllers */}
            <div className="flex border-b border-slate-100 gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('TERMS')}
                className={`py-2 px-3 text-xs font-bold transition-all relative border-b-2 cursor-pointer ${
                  activeTab === 'TERMS'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-slate-450 hover:text-slate-600'
                }`}
              >
                Terms & Conditions
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('PRIVACY')}
                className={`py-2 px-3 text-xs font-bold transition-all relative border-b-2 cursor-pointer ${
                  activeTab === 'PRIVACY'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-slate-450 hover:text-slate-600'
                }`}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('REFUND')}
                className={`py-2 px-3 text-xs font-bold transition-all relative border-b-2 cursor-pointer ${
                  activeTab === 'REFUND'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-slate-450 hover:text-slate-600'
                }`}
              >
                Refund Policy
              </button>
            </div>

            {/* Content panel */}
            <div className="text-xs text-slate-600 leading-relaxed font-medium space-y-4 max-h-[420px] overflow-y-auto pr-2">
              {activeTab === 'TERMS' && (
                <div className="space-y-3">
                  <h4 className="font-bold text-slate-800">Terms & Conditions of TRIA POS</h4>
                  <p>
                    Welcome to **TRIA POS**. The application and services are owned and operated by **KATIRTEK TRIA PVT LTD**. By accessing or using the TRIA POS software dashboard, cloud registry, or store management tools, you agree to comply with and be bound by the following terms.
                  </p>
                  <p className="font-bold text-slate-700">1. License & Use Constraints</p>
                  <p>
                    We grant each tenant and their authorized cashiers/staff users a non-exclusive, non-transferable right to access the software features according to their current subscription plan features (Starter/Silver, Professional/Gold, Enterprise/Platinum).
                  </p>
                  <p className="font-bold text-slate-700">2. Prohibited Behaviors</p>
                  <p>
                    Users must not reverse-engineer, decompile, or copy any software interface modules, source code structure, or database protocols. Disruption of multi-tenant cloud storage structures is strictly prohibited.
                  </p>
                  <p className="font-bold text-slate-700">3. Application Modifications</p>
                  <p>
                    **KATIRTEK TRIA PVT LTD** reserves the right to deliver updates, patches, feature enhancements, or gate capabilities based on licensing limits at any time to preserve safety and correct system telemetry.
                  </p>
                </div>
              )}

              {activeTab === 'PRIVACY' && (
                <div className="space-y-3">
                  <h4 className="font-bold text-slate-800">Privacy & Data Governance Policy</h4>
                  <p>
                    Your privacy is extremely critical to us. This privacy policy defines how **TRIA POS** (developed by **KATIRTEK TRIA PVT LTD**) collects, stores, and protects transaction data, customer records, and employee profiles.
                  </p>
                  <p className="font-bold text-slate-700">1. Data Storage & Multi-Tenancy</p>
                  <p>
                    All inventory products, barcode details, sales reports, customer loyalty points, and invoicing records are stored inside a securely isolated database schema matching the specific workspace tenant ID. No workspace data is visible or accessible by other tenants.
                  </p>
                  <p className="font-bold text-slate-700">2. Access Controls & Logs</p>
                  <p>
                    We record audit trails of activities (like password resets, licensing overrides, and deletions) to ensure security verification. Passwords are saved in the database strictly as salted, one-way cryptographically hashed bytes using `bcrypt`.
                  </p>
                  <p className="font-bold text-slate-700">3. Third-party Sharing</p>
                  <p>
                    **KATIRTEK TRIA PVT LTD** does not sell, lease, or share any inventory, sales, customer demographics, or financial files with third-party analytical companies or advertising networks.
                  </p>
                </div>
              )}

              {activeTab === 'REFUND' && (
                <div className="space-y-3 bg-rose-50/20 border border-rose-150 rounded-xl p-4">
                  <h4 className="font-bold text-rose-800">Refund Compliance Statement</h4>
                  <p className="font-bold text-rose-700">
                    Refund Policy: Once paid, there is no refund for the customer.
                  </p>
                  <p className="text-slate-600">
                    This refund rule governs all digital billing invoices, licensing cycles, subscription tier transitions, manual credit overrides, and customer receipts processed inside the **TRIA POS** terminal. 
                  </p>
                  <p className="text-slate-600">
                    By issuing credit tokens, cash returns, or processing checks in our software, the cashier and store administrator confirm that payment settlement is final. No transaction amount is subject to chargebacks, cash refunds, or cancellations once saved in the platform database.
                  </p>
                </div>
              )}
            </div>

            {/* Support footer */}
            <div className="border-t border-slate-100 pt-4 flex items-center justify-between gap-3 text-slate-500 text-xs">
              <span className="flex items-center gap-1.5">
                <Mail size={14} className="text-primary-600" />
                <span>Support Queries: <a href="mailto:support@katirtek.com" className="font-bold text-primary-600 hover:underline">support@katirtek.com</a></span>
              </span>
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                KATIRTEK TRIA PVT LTD
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
